import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { id: 0, name: "Sunday", short: "Sun" },
  { id: 1, name: "Monday", short: "Mon" },
  { id: 2, name: "Tuesday", short: "Tue" },
  { id: 3, name: "Wednesday", short: "Wed" },
  { id: 4, name: "Thursday", short: "Thu" },
  { id: 5, name: "Friday", short: "Fri" },
  { id: 6, name: "Saturday", short: "Sat" }
];

const TIME_SLOTS = [
  "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
  "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
  "18:00", "19:00", "20:00", "21:00"
];

function CourseSchedule({ user, config }) {
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showAddSession, setShowAddSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    subject_id: "",
    subject_name: "",
    day_of_week: 1,
    start_time: "09:00",
    duration: 60,
    notes: "",
    reminder: true
  });

  useEffect(() => {
    fetchSchedule();
    fetchSubjects();
  }, []);

  const fetchSchedule = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedule(response.data.sessions || []);
    } catch (err) {
      console.error("Error fetching schedule:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data.subjects || []);
    } catch (err) {
      console.error("Error fetching subjects:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.subject_id) {
      setError("Please select a subject");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      if (editingSession) {
        await axios.put(
          `${API}/schedule/${editingSession.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess("Schedule updated successfully!");
      } else {
        await axios.post(
          `${API}/schedule`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess("Study session added to schedule!");
      }

      setShowAddSession(false);
      setEditingSession(null);
      resetForm();
      fetchSchedule();
    } catch (err) {
      setError(err.response?.data?.detail || "Error saving schedule");
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Remove this study session from your schedule?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/schedule/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Session removed from schedule");
      fetchSchedule();
    } catch (err) {
      setError(err.response?.data?.detail || "Error removing session");
    }
  };

  const handleEdit = (session) => {
    setFormData({
      subject_id: session.subject_id,
      subject_name: session.subject_name,
      day_of_week: session.day_of_week,
      start_time: session.start_time,
      duration: session.duration,
      notes: session.notes || "",
      reminder: session.reminder
    });
    setEditingSession(session);
    setShowAddSession(true);
  };

  const resetForm = () => {
    setFormData({
      subject_id: "",
      subject_name: "",
      day_of_week: 1,
      start_time: "09:00",
      duration: 60,
      notes: "",
      reminder: true
    });
  };

  const getSessionsForDay = (dayId) => {
    return schedule
      .filter(s => s.day_of_week === dayId)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const getEndTime = (startTime, duration) => {
    const [hours, minutes] = startTime.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
  };

  const getTodaysSessions = () => {
    const today = new Date().getDay();
    return getSessionsForDay(today);
  };

  if (loading) {
    return (
      <div className="card">
        <h3>📅 Course Schedule</h3>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="course-schedule">
      {/* Header */}
      <div className="card" style={{
        background: "linear-gradient(135deg, #2a1a3a 0%, #1a2a3a 100%)",
        borderTop: "4px solid #DDA0DD"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
          <div>
            <h2 style={{ color: "#DDA0DD", margin: "0 0 5px 0" }}>
              📅 Course Schedule
            </h2>
            <p style={{ color: "#aaa", margin: 0 }}>
              Plan your weekly study sessions
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingSession(null);
              setShowAddSession(!showAddSession);
            }}
            data-testid="add-session-btn"
            style={{
              background: "#DDA0DD",
              color: "#000",
              marginTop: 0
            }}
          >
            + Add Study Session
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success" style={{ background: "#1a4d1a", color: "#90EE90", padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>{success}</div>}

      {/* Add/Edit Session Form */}
      {showAddSession && (
        <div className="card" style={{ background: "#1a1a2a", borderLeft: "4px solid #DDA0DD" }}>
          <h4 style={{ color: "#DDA0DD", margin: "0 0 20px 0" }}>
            {editingSession ? "Edit Study Session" : "Add Study Session"}
          </h4>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
              {/* Subject Selection */}
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>
                  Subject *
                </label>
                <select
                  value={formData.subject_id}
                  onChange={(e) => {
                    const subject = subjects.find(s => s.subject_id === e.target.value);
                    setFormData({
                      ...formData,
                      subject_id: e.target.value,
                      subject_name: subject?.subject_name || ""
                    });
                  }}
                  style={{ width: "100%", padding: "10px", background: "#0a0a0a", border: "1px solid #333", borderRadius: "6px", color: "#eee" }}
                  data-testid="subject-select"
                >
                  <option value="">Select a subject...</option>
                  {subjects.map((subject) => (
                    <option key={subject.subject_id} value={subject.subject_id}>
                      {subject.subject_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Day of Week */}
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>
                  Day of Week *
                </label>
                <select
                  value={formData.day_of_week}
                  onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                  style={{ width: "100%", padding: "10px", background: "#0a0a0a", border: "1px solid #333", borderRadius: "6px", color: "#eee" }}
                  data-testid="day-select"
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <option key={day.id} value={day.id}>{day.name}</option>
                  ))}
                </select>
              </div>

              {/* Start Time */}
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>
                  Start Time *
                </label>
                <select
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  style={{ width: "100%", padding: "10px", background: "#0a0a0a", border: "1px solid #333", borderRadius: "6px", color: "#eee" }}
                  data-testid="time-select"
                >
                  {TIME_SLOTS.map((time) => (
                    <option key={time} value={time}>{formatTime(time)}</option>
                  ))}
                </select>
              </div>

              {/* Duration */}
              <div>
                <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>
                  Duration (minutes)
                </label>
                <select
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                  style={{ width: "100%", padding: "10px", background: "#0a0a0a", border: "1px solid #333", borderRadius: "6px", color: "#eee" }}
                  data-testid="duration-select"
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", color: "#aaa" }}>
                Notes (optional)
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g., Focus on Chapter 3"
                style={{ width: "100%", padding: "10px", background: "#0a0a0a", border: "1px solid #333", borderRadius: "6px", color: "#eee" }}
              />
            </div>

            {/* Reminder Toggle */}
            <div style={{ marginTop: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="checkbox"
                id="reminder"
                checked={formData.reminder}
                onChange={(e) => setFormData({ ...formData, reminder: e.target.checked })}
                style={{ width: "20px", height: "20px" }}
              />
              <label htmlFor="reminder" style={{ color: "#aaa" }}>
                Enable reminders for this session
              </label>
            </div>

            {/* Form Buttons */}
            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <button type="submit" data-testid="save-session-btn" style={{ marginTop: 0 }}>
                {editingSession ? "Update Session" : "Add to Schedule"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddSession(false);
                  setEditingSession(null);
                  resetForm();
                }}
                style={{ background: "#333", marginTop: 0 }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Today's Sessions */}
      {getTodaysSessions().length > 0 && (
        <div className="card" style={{ background: "#1a2a1a", borderLeft: "4px solid #90EE90" }}>
          <h4 style={{ color: "#90EE90", margin: "0 0 15px 0" }}>
            📌 Today's Study Sessions
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {getTodaysSessions().map((session) => (
              <div
                key={session.id}
                style={{
                  background: "#0a1a0a",
                  padding: "15px",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div>
                  <div style={{ color: "#FFD700", fontWeight: "bold" }}>
                    {session.subject_name}
                  </div>
                  <div style={{ color: "#90EE90", fontSize: "0.9rem" }}>
                    {formatTime(session.start_time)} - {formatTime(getEndTime(session.start_time, session.duration))}
                  </div>
                  {session.notes && (
                    <div style={{ color: "#aaa", fontSize: "0.85rem", marginTop: "5px" }}>
                      📝 {session.notes}
                    </div>
                  )}
                </div>
                <button
                  style={{
                    background: "#90EE90",
                    color: "#000",
                    padding: "10px 20px",
                    marginTop: 0
                  }}
                >
                  Start Lesson →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Schedule Grid */}
      <div className="card">
        <h4 style={{ color: "#FFD700", margin: "0 0 20px 0" }}>
          📆 Weekly Schedule
        </h4>
        
        {schedule.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div style={{ fontSize: "4rem", marginBottom: "20px" }}>📅</div>
            <p style={{ color: "#aaa", marginBottom: "15px" }}>
              No study sessions scheduled yet.
            </p>
            <p style={{ color: "#999", fontSize: "0.9rem" }}>
              Click "Add Study Session" to plan your learning week!
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "flex", gap: "10px", minWidth: "800px" }}>
              {DAYS_OF_WEEK.map((day) => {
                const daySessions = getSessionsForDay(day.id);
                const isToday = new Date().getDay() === day.id;
                
                return (
                  <div
                    key={day.id}
                    style={{
                      flex: 1,
                      minWidth: "110px",
                      background: isToday ? "#1a2a1a" : "#1a1a1a",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: isToday ? "2px solid #90EE90" : "1px solid #333"
                    }}
                  >
                    {/* Day Header */}
                    <div style={{
                      background: isToday ? "#2a4a2a" : "#222",
                      padding: "10px",
                      textAlign: "center"
                    }}>
                      <div style={{ color: isToday ? "#90EE90" : "#FFD700", fontWeight: "bold" }}>
                        {day.short}
                      </div>
                      {isToday && (
                        <div style={{ color: "#90EE90", fontSize: "0.75rem" }}>Today</div>
                      )}
                    </div>
                    
                    {/* Sessions */}
                    <div style={{ padding: "10px", minHeight: "150px" }}>
                      {daySessions.length === 0 ? (
                        <div style={{ color: "#888", fontSize: "0.8rem", textAlign: "center", padding: "20px 5px" }}>
                          No sessions
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {daySessions.map((session) => (
                            <div
                              key={session.id}
                              data-testid={`session-${session.id}`}
                              onClick={() => handleEdit(session)}
                              style={{
                                background: "#0a0a0a",
                                padding: "8px",
                                borderRadius: "6px",
                                borderLeft: "3px solid #DDA0DD",
                                cursor: "pointer",
                                fontSize: "0.8rem"
                              }}
                            >
                              <div style={{ color: "#DDA0DD", fontWeight: "bold", marginBottom: "3px" }}>
                                {formatTime(session.start_time)}
                              </div>
                              <div style={{ color: "#eee", lineHeight: "1.3" }}>
                                {session.subject_name.length > 15 
                                  ? session.subject_name.substring(0, 15) + "..."
                                  : session.subject_name}
                              </div>
                              <div style={{ color: "#999", fontSize: "0.75rem", marginTop: "3px" }}>
                                {session.duration} min
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Schedule Stats */}
      {schedule.length > 0 && (
        <div className="card" style={{ background: "#1a1a2a" }}>
          <h4 style={{ color: "#87CEEB", margin: "0 0 15px 0" }}>
            📊 Schedule Summary
          </h4>
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <div>
              <span style={{ color: "#aaa" }}>Total Sessions: </span>
              <strong style={{ color: "#FFD700" }}>{schedule.length}</strong>
            </div>
            <div>
              <span style={{ color: "#aaa" }}>Weekly Study Time: </span>
              <strong style={{ color: "#90EE90" }}>
                {Math.round(schedule.reduce((sum, s) => sum + s.duration, 0) / 60)} hours
              </strong>
            </div>
            <div>
              <span style={{ color: "#aaa" }}>Subjects Scheduled: </span>
              <strong style={{ color: "#87CEEB" }}>
                {new Set(schedule.map(s => s.subject_id)).size}
              </strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseSchedule;
